import { Link, useLocation } from "@tanstack/react-router";
import { LogOut, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { useMemo } from "react";
import { navGroupsForRole } from "@/components/markyt/nav-config";
import { useSkillCatalog } from "@/hooks/use-skills";
import { signOut, useSession } from "@/lib/auth-client";
import { normalizeRole } from "@/lib/roles";
import { cn } from "@/lib/utils";

/** Brand + role-gated nav + sign-out. Shared by the persistent aside and the mobile drawer. */
export function SidebarNav({
  onNavigate,
  collapsed = false,
  onToggleCollapse,
}: {
  onNavigate?: () => void;
  /** Desktop icon-rail mode. Always expanded inside the mobile drawer. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}) {
  const { data: session } = useSession();
  const location = useLocation();
  const role = normalizeRole(session?.user?.role);
  const groups = useMemo(() => navGroupsForRole(role), [role]);
  const { data: catalog } = useSkillCatalog();
  const publishedCount = catalog?.length ?? 0;

  return (
    <div className="flex h-full w-full flex-col bg-navy text-white">
      {/* Brand lockup */}
      <div
        className={cn("flex items-center px-5 py-5", collapsed ? "justify-center px-3" : "gap-3")}
      >
        <img src="/nymbl-fox-white-btn.png" alt="" width={30} height={30} className="shrink-0" />
        {!collapsed && (
          <div className="leading-tight">
            <div className="font-display text-[19px] font-bold">Markyt</div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan">
              Skill Library
            </div>
          </div>
        )}
      </div>

      {/* Desktop collapse toggle (absent in the mobile drawer) */}
      {onToggleCollapse && (
        <button
          type="button"
          onClick={onToggleCollapse}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-pressed={collapsed}
          title={collapsed ? "Expand navigation" : "Collapse navigation"}
          className={cn(
            "mx-3 mb-1 flex items-center gap-3 rounded-[9px] px-3 py-2 text-sm font-medium text-white/55 transition-colors hover:bg-white/5 hover:text-white",
            collapsed && "justify-center px-0",
          )}
        >
          {collapsed ? (
            <PanelLeftOpen className="size-4 shrink-0" />
          ) : (
            <PanelLeftClose className="size-4 shrink-0" />
          )}
          {!collapsed && <span>Collapse</span>}
        </button>
      )}

      <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-2">
        {groups.map((group) => (
          <div key={group.title}>
            {!collapsed && (
              <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-white/55">
                {group.title}
              </div>
            )}
            <div className="space-y-0.5">
              {group.items.map((item) => {
                const active =
                  item.href === "/"
                    ? location.pathname === "/"
                    : location.pathname.startsWith(item.href);
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onNavigate}
                    title={collapsed ? item.label : undefined}
                    className={cn(
                      "flex items-center gap-3 rounded-[9px] px-3 py-2 text-sm font-medium transition-colors",
                      collapsed && "justify-center px-0",
                      active
                        ? "bg-[rgba(64,126,193,0.22)] text-white shadow-[inset_2px_0_0_var(--nymbl-g-cyan)]"
                        : "text-white/70 hover:bg-white/5 hover:text-white",
                    )}
                  >
                    <item.icon className="size-4 shrink-0" />
                    {!collapsed && <span>{item.label}</span>}
                    {!collapsed && item.count === "published" && (
                      <span className="ml-auto rounded-full bg-white/10 px-1.5 py-0.5 text-[11px] font-semibold tabular-nums text-white/80">
                        {publishedCount}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t border-white/10 p-3">
        {!collapsed && session?.user && (
          <div className="mb-2 px-3">
            <p className="truncate text-sm font-medium text-white">{session.user.name}</p>
            <p className="truncate text-xs capitalize text-white/60">{role}</p>
          </div>
        )}
        <button
          type="button"
          onClick={() => void signOut()}
          title={collapsed ? "Sign out" : undefined}
          aria-label="Sign out"
          className={cn(
            "flex w-full items-center gap-3 rounded-[9px] px-3 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white",
            collapsed && "justify-center px-0",
          )}
        >
          <LogOut className="size-4 shrink-0" />
          {!collapsed && <span>Sign out</span>}
        </button>
      </div>
    </div>
  );
}
